import React from "react";

/**
 * LoadingBlock — skeleton composito che sostituisce lo spinner centrale.
 *
 * Varianti:
 *   - "grid": griglia 4-col di card skeleton (default)
 *   - "list": lista verticale di righe skeleton
 *   - "table": righe tabella skeleton
 *   - "page": placeholder di intera pagina (header + hero + grid)
 *   - "chat": bolle chat skeleton
 *   - "kpis": 4 KPI cards skeleton
 *
 * Uso:
 *   {loading ? <LoadingBlock variant="grid" count={8} /> : <ActualContent/>}
 */

function SkelCard() {
  return (
    <div className="sk-card">
      <div className="flex items-start gap-3">
        <div className="sk-circle w-10 h-10 shrink-0" />
        <div className="flex-1 space-y-2 min-w-0">
          <div className="sk-line w-3/4" />
          <div className="sk-line sk-line-sm w-1/2" />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <div className="sk-line sk-line-sm w-full" />
        <div className="sk-line sk-line-sm w-5/6" />
      </div>
      <div className="mt-4 pt-3 border-t border-slate-100 flex gap-2">
        <div className="sk-line sk-line-sm w-16" />
        <div className="sk-line sk-line-sm w-20" />
      </div>
    </div>
  );
}

function SkelRow() {
  return (
    <div className="flex items-center gap-3 py-3 border-b border-slate-100">
      <div className="sk-circle w-8 h-8 shrink-0" />
      <div className="flex-1 space-y-2 min-w-0">
        <div className="sk-line w-1/3" />
        <div className="sk-line sk-line-sm w-1/2" />
      </div>
      <div className="sk-line w-16 shrink-0" />
    </div>
  );
}

function SkelTableRow({ cols = 6 }) {
  return (
    <tr className="border-b border-slate-100">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-3 py-3">
          <div className={`sk-line ${i === 0 ? "w-2/3" : i === cols - 1 ? "w-12" : "w-4/5"}`} />
        </td>
      ))}
    </tr>
  );
}

function SkelKpi() {
  return (
    <div className="sk-card">
      <div className="flex items-center justify-between">
        <div className="sk-line sk-line-sm w-20" />
        <div className="sk-circle w-6 h-6" />
      </div>
      <div className="sk-line sk-line-lg w-32 mt-4" style={{ height: 28 }} />
      <div className="sk-line sk-line-sm w-16 mt-3" />
    </div>
  );
}

function SkelChatBubble({ side = "left" }) {
  const align = side === "right" ? "ml-auto" : "";
  const w = side === "right" ? "w-1/2" : "w-2/3";
  return (
    <div className={`${align} ${w} space-y-2 mb-4`}>
      <div className="sk-line w-full" style={{ height: 16 }} />
      <div className="sk-line w-4/5" style={{ height: 16 }} />
      <div className="sk-line sk-line-sm w-2/5" />
    </div>
  );
}

export default function LoadingBlock({ variant = "grid", count, className = "", cols = 6 }) {
  const wrapper = `animate-fade-in ${className}`;

  if (variant === "list") {
    const n = count ?? 6;
    return (
      <div className={wrapper} data-testid="loading-block-list">
        {Array.from({ length: n }).map((_, i) => <SkelRow key={i} />)}
      </div>
    );
  }

  if (variant === "table") {
    const n = count ?? 8;
    return (
      <table className={`w-full ${wrapper}`} data-testid="loading-block-table">
        <tbody>
          {Array.from({ length: n }).map((_, i) => <SkelTableRow key={i} cols={cols} />)}
        </tbody>
      </table>
    );
  }

  if (variant === "kpis") {
    const n = count ?? 4;
    return (
      <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 ${wrapper}`} data-testid="loading-block-kpis">
        {Array.from({ length: n }).map((_, i) => <SkelKpi key={i} />)}
      </div>
    );
  }

  if (variant === "chat") {
    return (
      <div className={`space-y-3 ${wrapper}`} data-testid="loading-block-chat">
        <SkelChatBubble side="left" />
        <SkelChatBubble side="right" />
        <SkelChatBubble side="left" />
      </div>
    );
  }

  if (variant === "page") {
    return (
      <div className={`space-y-6 ${wrapper}`} data-testid="loading-block-page">
        <div className="space-y-3">
          <div className="sk-line sk-line-sm w-24" />
          <div className="sk-line sk-line-lg w-1/3" style={{ height: 28 }} />
          <div className="sk-line sk-line-sm w-1/2" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <SkelKpi key={i} />)}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <SkelCard key={i} />)}
        </div>
      </div>
    );
  }

  // default: grid
  const n = count ?? 8;
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ${wrapper}`} data-testid="loading-block-grid">
      {Array.from({ length: n }).map((_, i) => <SkelCard key={i} />)}
    </div>
  );
}
